import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight, Shield, Star, CheckCircle } from 'lucide-react';
import { HERO_DATA, BRAND } from '../../data/mock';

export const HeroSection = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-teal-50/30">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal-100 rounded-full opacity-50 blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-cyan-100 rounded-full opacity-40 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-left">
            <Badge 
              variant="outline" 
              className="mb-6 px-4 py-2 border-teal-200 bg-teal-50 text-teal-700 font-medium"
            >
              <Shield className="w-4 h-4 mr-2" />
              Licensed in FL, NC & VA
            </Badge>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              {HERO_DATA.headline}
            </h1>

            <p className="text-lg md:text-xl text-slate-600 mb-6 leading-relaxed">
              {HERO_DATA.subheadline}
            </p>

            <div className="flex items-center gap-2 text-sm text-slate-500 mb-8">
              <CheckCircle className="w-5 h-5 text-teal-600" />
              <span>{HERO_DATA.trustLine}</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                asChild
                size="lg"
                className="text-lg px-8 py-6 text-white font-bold shadow-xl shadow-orange-500/30 hover:shadow-2xl hover:shadow-orange-500/40 transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #f97316 0%, #fb7185 100%)' }}
              >
                <a href={BRAND.quoteUrl} target="_blank" rel="noopener noreferrer">
                  {HERO_DATA.ctaText}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="text-lg px-8 py-6 border-2 border-slate-200 hover:border-teal-300 hover:bg-teal-50 text-slate-700 font-semibold transition-all"
                onClick={() => document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                See How It Works
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <span className="text-sm text-slate-600 font-medium">Trusted by Pet Parents</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Shield className="w-4 h-4 text-teal-600" />
                Backed by Prudent Pet Insurance
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={HERO_DATA.image}
                alt="Happy pet owners with their dog and cat"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
            </div>
            
            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 max-w-xs hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">No Added Cost</p>
                  <p className="text-sm text-slate-500">Broker service is free to you</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
